import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import moment from 'moment';

// models
import { FunctionalType } from '../../../core/models/blockedFunctionalType';
import { ServiceFeatures } from '../../../core/models/serviceFeatures';

// services
import { BlockedFunctionalService } from '../../../core/services/blocked-functional.service';
import { FiltersService } from '../../../core/services/filters.service';
import { PlanogramsService } from '../../../core/services/planograms.service';
import { PlansService } from '../../../core/services/plans.service';
import { SessionService } from '../../../core/services/session.service';
import { StoresService } from '../../../core/services/stores.service';
import { Utils } from '../../../core/services/utils.service';

type PlanogramsTable = { columns: Array<string>, data: Array<any> }
type PlanOption = { id: number; name: string };

@Component({
    selector: 'app-copy-planogram-modal',
    templateUrl: './copy-planogram-modal.component.html',
    styleUrls: ['./copy-planogram-modal.component.scss'],
    standalone: false
})
export class CopyPlanogramModalComponent implements OnInit {

	selected_planograms: any[] = [];

	search_str: string = '';
	search_plan: any = -1;

	smart_type: string;
	smart_copy_types: any[] = [
		{
			type: 'QTY',
			description: 'SMART_COPY_SALES_DESCRIPTION'
		},
		{
			type: 'STOCK_QTY',
			description: 'SMART_COPY_STOCK_DESCRIPTION'
		},
		{
			type: 'SMART_COPY_FACES_TITLE',
			description: 'SMART_COPY_FACE_DESCRIPTION'
		}
	];

	show_smart_copy: boolean = false;
	dateOpt: any = {};
	date_obj: any = {
		date_from: this.sessionService.defaults.date_to ?
			moment(this.sessionService.defaults.date_to).subtract('180', 'day').format('YYYY-MM-DD') :
			this.sessionService.defaults.date_to,
		date_to: this.sessionService.defaults.date_to
	};
	reversingEnabled: boolean = false;

	show_filters: boolean = false;
	searchQuery: string = '';
	checked_markers: any[] = [];
	checked_groups: any[] = [];

	originalPlanograms: Array<any> = [];
	originalShopGroups: Array<any> = [];
	originalShopMarkers: Array<any> = [];
	planogramsTable: PlanogramsTable = { columns: [], data: [] };
	plans: Array<PlanOption> = [];
	shopGroups: Array<any> = [];
	shopMarkers: Array<any> = [];
	showLoader: boolean = false;

	protected readonly features: typeof ServiceFeatures = ServiceFeatures;

	constructor(
		private dialogRef: MatDialogRef<CopyPlanogramModalComponent>,
		private blockedFunctionalService: BlockedFunctionalService,
		public filtersService: FiltersService,
		private planogramsService: PlanogramsService,
		private plansService: PlansService,
		private sessionService: SessionService,
		private storesService: StoresService
	) {
		if (this.sessionService.defaults.date_from && this.sessionService.defaults.date_to) {
			this.dateOpt = {
				min: moment(this.sessionService.defaults.date_from),
				max: moment(this.sessionService.defaults.date_to),
				date_from: filtersService.Filters.date_from,
				date_to: filtersService.Filters.date_to
			};
		}
	}

	ngOnInit() {
		this.plansService
			.getPlans<PlanOption>({}, ['id', 'name'])
			.subscribe((plans: Array<PlanOption>) => {
				this.plans = [{ id: -1, name: 'ALL'}, ...plans.sort(Utils.sortBy('name'))];
			});

		this.storesService.getShopGroups().then((shopGroups: Array<any>) => {
			if (shopGroups) {
				Utils.rec(shopGroups, (i) => (i.id = i.group_id));
				this.originalShopGroups.push(...shopGroups);
				this.formatData(Utils.deepCopy(this.originalShopGroups), 'shopGroups');
			}
		});

		this.storesService.getShopMarkers().then((shopMarkers: Array<any>) => {
			if (shopMarkers) {
				this.originalShopMarkers.push(...shopMarkers);
				this.formatData(Utils.deepCopy(this.originalShopMarkers), 'shopMarkers');
			}
		});

		this.showLoader = true;
		this.planogramsService.getAllStands().then((data) => {
			this.originalPlanograms.push(...data);
			this.buildTable();
			this.showLoader = false;
		});
	}

	setDate(evt) {
		this.date_obj.date_from = evt.date_from;
		this.date_obj.date_to = evt.date_to;
	}

	filterMarkers() {
		if (~this.search_plan) {
			const plan = this.originalPlanograms.find(i => i.planogram_id == this.search_plan);

			if (!plan) {
				this.formatData([], 'shopMarkers');
				return;
			}

			this.formatData(
				Utils.deepCopy(
					this.originalShopMarkers
						.filter(i => i.id == plan.format_id)
						.map(i => ({ id: i.id, name: i.name }))
				),
				'shopMarkers');
		} else {
			this.formatData(Utils.deepCopy(this.originalShopMarkers), 'shopMarkers');
		}
	}

	buildTable() {
		this.planogramsTable = {
			columns: ['PLANOGRAM_NAME', 'PLAN_NAME', 'RETAIL_STORE'],
			data: this.originalPlanograms
				.filter(i => ~this.search_plan ? i.planogram_id == this.search_plan : true)
				.filter(i =>
					Utils.searchItem(
						i,
						['stand_name'],
						this.search_str,
						this.sessionService.personalSettings.searchAlgorithm
					)
				)
				.filter(i => this.checked_markers.length ? this.checked_markers.some(m => i.markers.includes(m.id)) : true)
				.filter(i => this.checked_groups.length ? this.checked_groups.some(g => g.id == i.group_id) : true)
				.map(i => {
					const output = [
						{
							data: i.stand_name,
							stand_id: i.stand__main_stand__id,
							planogram_id: i.planogram_id
						},
						{
							data: i.planogram_name
						},
						{
							data: i.shop_name
						}
					];

					(output as any).checked = !!this.selected_planograms.find(selected =>
						selected.stand__main_stand__id == i.stand__main_stand__id &&
						selected.planogram_id == i.planogram_id
					);
					output['id'] = i.stand__main_stand__id;
					output['planogram_id'] = i.planogram_id;

					return output;
				})
		};
	}

	getSelectedPlanograms() {
		this.selected_planograms = this.originalPlanograms.filter(s => s.selected);

		return this.selected_planograms;
	}

	onSelectedAll(value) {
		this.planogramsTable.data.map(el => el.checked = value);
		const tempArr = this.planogramsTable.data.map(s =>
			this.originalPlanograms.find(i =>
				i.stand__main_stand__id == s[0].stand_id &&
				i.planogram_id == s[0].planogram_id
			)
		);

		if (value) {
			this.selected_planograms = [...tempArr];
		} else {
			if (this.planogramsTable.data.length == this.originalPlanograms.length) {
				this.selected_planograms = [];
			} else {
				this.selected_planograms = this.selected_planograms.filter(row => {
					if (!tempArr.find(el => el.stand__main_stand__id == row.stand__main_stand__id && el.planogram_id == row.planogram_id)) {
						return row;
					}
				});
			}
		}
		this.planogramsTable = Object.assign({}, this.planogramsTable);
	}

	onSelect(value) {
		this.selected_planograms.push(this.originalPlanograms.find(i =>
			i.stand__main_stand__id == value.data[0].value.stand_id &&
			i.planogram_id == value.data[0].value.planogram_id
		));
	}

	unSelect(value) {
		this.selected_planograms = this.selected_planograms.filter(i => i.stand__main_stand__id != value.data[0].value.stand_id ||
			i.planogram_id != value.data[0].value.planogram_id);
	}

	remove(item) {
		this.selected_planograms = this.selected_planograms.filter(i => !Utils.equalObjects(item, i));

		this.buildTable();
	}

	close(): void {
		if (this.show_smart_copy) {
			this.show_smart_copy = false;
			this.smart_type = null;
			this.dialogRef.updateSize('70%');
			return;
		}
		this.dialogRef.close(null);
	}

	apply() {
		if (!this.selected_planograms.length) return;
		if (this.show_smart_copy && !this.smart_type) return;

		return this.dialogRef.close(
			this.selected_planograms.length ?
				{
					type: this.smart_type,
					planograms: this.selected_planograms
						.map(i => {
							return { planogram_id: i.stand__main_stand__id, plan_id: i.planogram_id };
						}),
					date_filters: this.date_obj,
					isReversed: this.reversingEnabled,
					library: this.originalPlanograms
				} : null
		);
	}

	showProductSmartCopy() {
		if (!this.sessionService.hasFeature(ServiceFeatures.PlanogramExtendedCopy)) {
			this.blockedFunctionalService.openBlockedFunctionalModal(FunctionalType.PlanogramExtendedCopying);
			return;
		}

		if (!this.selected_planograms.length) return;

		this.dialogRef.updateSize('40%');
		this.show_smart_copy = true;
	}

	toggleFilters() {
		this.show_filters = !this.show_filters;
	}

	applyFilters() {
		this.buildTable();
		this.toggleFilters();
	}

	clearFilters(toggle: boolean = true) {
		this.checked_groups = [];
		this.checked_markers = [];

		this.formatData(Utils.deepCopy(this.originalShopMarkers), 'shopMarkers');
		this.formatData(Utils.deepCopy(this.originalShopGroups), 'shopGroups');

		this.buildTable();
		if (toggle) this.toggleFilters();
	}

	onCheck(element: any, type: string) {
		element.checked = !element.checked;
		Utils.childrenCheck(element);
		Utils.parentCheck(element);

		// якшо вибрано "Всі"
		if (!~element.id) {
			this[type] = [];
			Utils.rec(element.children, c => {
				if (element.checked) {
					this[type].push({ id: c.id, name: c.name });
				} else {
					this[type] = this[type].filter(s => s.id != c.id);
				}
			});
			return;
		}

		const children = (element.children || []).map(c => ({ id: c.id, name: c.name }));

		if (!element.parent && !children.length) {
			children.push({ id: element.format_id, name: element.name });
		}

		if (element.parent) {
			children.push({ id: element.id || element.shop_id || element.format_id, name: element.name });
		}

		if (element.checked) {
			this[type] = this[type].concat(children);
		} else {
			this[type] = this[type].filter(s => !children.some(item => s.id == item.id && s.name == item.name));
		}
	}

	searchStoreGroups(search: string) {
		this.searchQuery = search;
		if (search.length) {
			this.shopGroups = this.findElement(this.formatData(Utils.deepCopy(this.originalShopGroups), 'shopGroups')[0].children, search);
			Utils.recursiveOpened(this.shopGroups);
			return;
		}

		this.formatData(Utils.deepCopy(this.originalShopGroups), 'shopGroups');
	}

	searchStoreMarkers(search: string) {
		this.searchQuery = search;
		if (search.length) {
			this.shopMarkers = this.findElement(this.formatData(Utils.deepCopy(this.originalShopMarkers), 'shopMarkers')[0].children, search);
			Utils.recursiveOpened(this.shopMarkers);
			return;
		}

		this.formatData(Utils.deepCopy(this.originalShopMarkers), 'shopMarkers');
	}

	findElement(element: any, searchQuery: string) {
		return element.filter(el => {
			if (
				Utils.searchItem(
					el,
					['name'],
					searchQuery,
					this.sessionService.personalSettings.searchAlgorithm
				)
			) {
				return el;
			} else if (el.children.some(e =>
				Utils.searchItem(
					e,
					['name'],
					searchQuery,
					this.sessionService.personalSettings.searchAlgorithm
				)
			)) {
				el.children = el.children.filter(e =>
					Utils.searchItem(
						e,
						['name'],
						searchQuery,
						this.sessionService.personalSettings.searchAlgorithm
					)
				);
				return el;
			}
		});
	}

	formatData(data, type) {
		const parent: any = {
			id: -1,
			name: 'ALL',
			has_children: true,
			level: 1,
			opened: true
		};

		parent.children = this.formatChildren(data, parent);

		this[type] = [parent];

		Utils.rec(this[type], e => {
			e.checked = this[type == 'shopMarkers' ? 'checked_markers' : type == 'shopGroups' ? 'checked_groups' : 'checked_statuses']
				.some(item => item.id == e.id && item.name == e.name);

			if (e.checked) {
				Utils.childrenCheck(e);
				Utils.parentCheck(e);
			}
		});

		return this[type];
	}

	formatChildren(list, parent) {
		return list.map(item => {
			item.id = item.id || Number(item.group_id || item.format_id);
			item.name = item.name || item.format_name;
			item.has_children = !!(item.children && item.children.length);
			item.level = parent.level + 1;
			item.parent = parent;

			if (item.has_children) item.children = this.formatChildren(item.children, item);

			return item;
		});
	}

	getModalTitle(): string {
		return this.originalShopMarkers.length ? 'SHOP-FORMAT' : this.originalShopGroups.length ? 'SHOP-GROUP' : '';
	}
}
